/**
 * The one place that turns a managed agent's saved `modelOptions` into the
 * option block that travels on a `ModelSelection`.
 *
 * Selecting an agent *imposes* its effort: the person running the turn stops
 * choosing effort, `thinking` and `fastMode`, and whatever the agent leaves
 * unset falls to the default of the agent's own model rather than back to the
 * person's last pick. An agent is a unit of behaviour an admin approves, and how
 * deeply — and expensively — it reasons is part of that behaviour.
 *
 * **Why one shared module and not two implementations.** The composer decides
 * whether to dispatch `thread.meta.update` before a turn by comparing the
 * selection it is about to run against the thread's stored one, with
 * `modelSelectionDiffers` in `./model.ts`. The server's composer-queue
 * dispatcher replays the same comparison. If the client computed an agent's
 * options one way and the server another, *every* turn under an agent would
 * write a meta event that changes nothing — a projection write and a broadcast
 * per turn, forever.
 *
 * **Why the resolved block omits the model's default.** `normalizeCodexModelOptions`
 * and `normalizeClaudeModelOptions` already drop an effort equal to the model
 * default, and other paths run selections through them. Emitting the default
 * explicitly here would mean this module and those normalizers disagreed on the
 * same intent, which is the disagreement described above. Omitting it produces
 * the identical result at the provider — the adapters fall back to the model
 * default for an absent effort — while keeping one canonical encoding.
 *
 * `describeAgentEffectiveTraits` is the exception: the disabled composer control
 * has to *show* a value, and "nothing" is not a value a person can read. It
 * returns the defaults spelt out, and is for display only — never feed its
 * output back into a selection.
 */
import {
  type ClaudeModelOptions,
  type CodexModelOptions,
  type ManagedAgentId,
  type ModelSelection,
  type ProviderKind,
  type ProviderModelOptions,
} from "@taskin-code/contracts";

import {
  getDefaultEffort,
  getModelCapabilities,
  normalizeClaudeModelOptions,
  normalizeCodexModelOptions,
} from "./model";

/**
 * The parts of a managed agent this module needs.
 *
 * A structural subset rather than `ManagedAgent` so the server's
 * `ManagedAgentRecord` — which spells the id `agentId`, not `id` — can be passed
 * without a translation step at every call site. Callers pass `{ ...record, id:
 * record.agentId }` or the wire object as-is.
 */
export interface AgentModelPin {
  readonly provider: ProviderKind;
  readonly model: string;
  readonly modelOptions?: ProviderModelOptions | null | undefined;
}

/** As above, plus the id that has to ride on the resulting selection. */
export interface IdentifiedAgentModelPin extends AgentModelPin {
  readonly id: ManagedAgentId;
}

/**
 * The option block to put on a `ModelSelection` for this agent, or `undefined`
 * when the agent runs its model's defaults.
 *
 * Runs through the same normalizers as every other selection, so an effort that
 * *is* the model's default collapses to `undefined` and a Claude effort the model
 * no longer offers is dropped. (The Codex normalizer does not filter against the
 * ladder; `CodexAdapter` does, per turn.)
 *
 * **Except for a model outside the catalogue**, where the block is passed
 * through untouched. `getModelCapabilities` answers with an empty ladder for a
 * slug it does not know, and `normalizeClaudeModelOptions` reads that as "the
 * model offers no efforts" and drops the pin — so an agent on a hand-typed slug
 * would save an effort in Settings and silently never run it. The agent form
 * accepts such slugs on purpose, and the write path validates nothing for them
 * for the same reason: unknown model, no opinion. The adapter still degrades if
 * the provider rejects it.
 */
export function resolveAgentModelOptions(
  agent: AgentModelPin,
): CodexModelOptions | ClaudeModelOptions | undefined {
  const pinned =
    agent.provider === "codex" ? agent.modelOptions?.codex : agent.modelOptions?.claudeAgent;
  if (!pinned) return undefined;

  const caps = getModelCapabilities(agent.provider, agent.model);
  if (caps.reasoningEffortLevels.length === 0 && !caps.supportsThinkingToggle) {
    return Object.keys(pinned).length > 0 ? pinned : undefined;
  }

  return agent.provider === "codex"
    ? normalizeCodexModelOptions(agent.model, pinned as CodexModelOptions)
    : normalizeClaudeModelOptions(agent.model, pinned as ClaudeModelOptions);
}

/**
 * The complete selection for running a turn as this agent.
 *
 * The cast is the same one every other construction site of a `ModelSelection`
 * makes: `provider` is a `ProviderKind` here rather than the literal the union's
 * discriminant wants, and narrowing it would mean branching on the provider to
 * build two structurally identical objects.
 */
export function buildAgentModelSelection(agent: IdentifiedAgentModelPin): ModelSelection {
  const options = resolveAgentModelOptions(agent);
  return {
    provider: agent.provider,
    model: agent.model,
    agentId: agent.id,
    ...(options ? { options } : {}),
  } as ModelSelection;
}

/**
 * Apply an agent's pin to a selection that already names it.
 *
 * The selection's own `provider`/`model` win: by the time this runs they have
 * been through the model-policy chain, which may have rewritten a retired slug
 * or refused a provider for this user. Only `options` comes from the agent — and
 * it *replaces* whatever was there, rather than filling a gap, because the whole
 * point is that the caller does not get a say.
 */
export function applyAgentModelOptions(
  selection: ModelSelection,
  agent: AgentModelPin,
): ModelSelection {
  const options = resolveAgentModelOptions({
    provider: selection.provider,
    model: selection.model,
    modelOptions: agent.modelOptions,
  });
  // Rebuilt field by field rather than spread-minus-`options`: the union has
  // exactly these four members, and an explicit list makes it impossible for a
  // future field to be carried across by accident.
  return {
    provider: selection.provider,
    model: selection.model,
    ...(selection.agentId ? { agentId: selection.agentId } : {}),
    ...(options ? { options } : {}),
  } as ModelSelection;
}

export interface AgentEffectiveTraits {
  /** The effort the turn will actually run at, or `null` for a model with no ladder. */
  readonly effort: string | null;
  /** `null` when the model has no thinking toggle to show. */
  readonly thinking: boolean | null;
  readonly fastMode: boolean;
}

/**
 * What the disabled composer controls should display for this agent.
 *
 * Unlike `resolveAgentModelOptions`, defaults are spelt out: a control showing
 * nothing tells the person nothing, and the reason it is disabled is precisely
 * that they need to know what the agent will do.
 */
export function describeAgentEffectiveTraits(agent: AgentModelPin): AgentEffectiveTraits {
  const caps = getModelCapabilities(agent.provider, agent.model);
  const resolved = resolveAgentModelOptions(agent);
  const pinnedEffort =
    agent.provider === "codex"
      ? (resolved as CodexModelOptions | undefined)?.reasoningEffort
      : (resolved as ClaudeModelOptions | undefined)?.effort;
  return {
    effort: pinnedEffort ?? getDefaultEffort(caps),
    thinking: caps.supportsThinkingToggle
      ? (agent.modelOptions?.claudeAgent?.thinking ?? true)
      : null,
    fastMode: (resolved as { fastMode?: boolean } | undefined)?.fastMode === true,
  };
}
